import React, { useEffect } from 'react';

import Button from 'components/Button';
import { ButtonType } from 'components/Button/Button';
import Modal from 'components/Common/Modal';
import { LowAnnualIncomeConfirmation } from 'components/Verification/Steps/EmploymentDetails/employmentDetailsUtils';

import styles from './LowAnnualIncomeConfirmModal.module.scss';

export enum LowAnnualIncomeChoice {
  Annualized = 'Annualized Per Year',
  Entered = 'Entered Per Year',
  Other = 'Other',
}

interface LowAnnualIncomeConfirmModalProps {
  confirmation: LowAnnualIncomeConfirmation;
  onClose: () => void;
  onConfirm: (choice: LowAnnualIncomeChoice) => void;
}

const LowAnnualIncomeConfirmModal = ({ confirmation, onClose, onConfirm }: LowAnnualIncomeConfirmModalProps) => {
  const { enteredIncomeLabel, annualizedIncomeLabel, payFrequencyLabel } = confirmation;

  useEffect(() => {
    analytics.track('Low Annual Income Confirmation Displayed', {
      enteredIncome: confirmation.enteredIncome,
      annualizedIncome: confirmation.annualizedIncome,
      payFrequency: payFrequencyLabel,
    });
  }, [confirmation.annualizedIncome, confirmation.enteredIncome, payFrequencyLabel]);

  const handleChoice = (choice: LowAnnualIncomeChoice) => {
    analytics.track('Low Annual Income Confirmation Selected', {
      option: choice,
      enteredIncome: confirmation.enteredIncome,
      annualizedIncome: confirmation.annualizedIncome,
      payFrequency: payFrequencyLabel,
    });
    onConfirm(choice);
  };

  const handleClose = () => {
    analytics.track('Low Annual Income Confirmation Dismissed', {
      enteredIncome: confirmation.enteredIncome,
      annualizedIncome: confirmation.annualizedIncome,
      payFrequency: payFrequencyLabel,
    });
    onClose();
  };

  return (
    <Modal closeModal={handleClose} usePortal>
      <div className={styles.content}>
        <h3 className={styles.title}>Please confirm your annual income</h3>
        <p className={styles.message}>This information is needed to put together your offer.</p>
        <div className={styles.buttonContainer}>
          <Button
            type={ButtonType.OptionButton}
            className={styles.choice}
            onClick={() => handleChoice(LowAnnualIncomeChoice.Annualized)}
          >
            {annualizedIncomeLabel} per year
          </Button>
          <Button
            type={ButtonType.OptionButton}
            className={styles.choice}
            onClick={() => handleChoice(LowAnnualIncomeChoice.Entered)}
          >
            {enteredIncomeLabel} per year
          </Button>
          <Button
            type={ButtonType.OptionButton}
            className={styles.choice}
            onClick={() => handleChoice(LowAnnualIncomeChoice.Other)}
          >
            Other
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LowAnnualIncomeConfirmModal;
